import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { aiActivity, pathForView, type AiActivityResponse, type AiOutcome, type ViewId } from '@aithora/shared';
import { useSession } from '../session/SessionContext';
import { useQuery } from '../useQuery';
import { sx } from '../ui/designStyles';
import { hm, useAiControls } from './aiControls';

/**
 * The Atlas panel's Activity tab (Console V4, Spec V4 section 16 D; design T19010-19047,
 * values L35880-35939): every AI event in scope, newest first, from GET /ai/activity (the
 * Truth Ledger's AI events). Filters (Mine / Everyone, outcome chips with counts, a
 * facility search for Aithora roles) and a health strip computed over the filtered rows.
 * The server scopes the rows (every building for Super Admin and Operator, the viewer's
 * building otherwise); the page only filters what it was sent.
 */

const OT: Record<AiOutcome, [string, string]> = {
  RAN: ['#264C7E', 'rgba(47,92,151,.12)'],
  PROPOSED: ['#54606C', 'rgba(34,39,46,.08)'],
  APPROVED: ['#12503C', 'rgba(31,107,82,.13)'],
  EDITED: ['#2F5C97', 'rgba(47,92,151,.08)'],
  DECLINED: ['#8A1C16', 'rgba(179,38,30,.1)'],
  REFUSED: ['#3C454F', 'rgba(34,39,46,.13)'],
  HELD: ['#8A1C16', '#F6E3E1'],
  PAUSED: ['#8A1C16', '#F6E3E1'],
  // V5: what a person's rules did (a chip shows only when a row carries it).
  AUTO: ['#12503C', 'rgba(31,107,82,.13)'],
  WAITING: ['#7A4A0E', 'rgba(194,112,47,.16)'],
  PRACTICE: ['#5B3A8C', 'rgba(122,106,174,.16)'],
  UNDONE: ['#3C454F', 'rgba(34,39,46,.13)'],
};
const ORDER: ('All' | AiOutcome)[] = ['All', 'RAN', 'AUTO', 'WAITING', 'PRACTICE', 'UNDONE', 'PROPOSED', 'APPROVED', 'EDITED', 'DECLINED', 'REFUSED', 'HELD', 'PAUSED'];
const word = (o: string) => o.charAt(0) + o.slice(1).toLowerCase();
const pill = (on: boolean) =>
  'display:inline-flex;align-items:center;gap:5px;height:26px;padding:0 10px;border-radius:7px;font-size:14px;font-weight:600;text-decoration:none;white-space:nowrap;' +
  (on ? 'background:#264C7E;color:#fff;' : 'border:1px solid rgba(34,39,46,.16);color:#3C454F;');
const VIEWS = new Set(['overview', 'residents', 'guardian', 'worklist', 'oversight', 'finance', 'capture', 'performance', 'atlasIntel', 'policy', 'pulse', 'sales', 'family', 'atlas', 'settings', 'companion']);

export function AtlasActivity({ team, onQueue, onClose }: { team: boolean; onQueue: () => void; onClose: () => void }) {
  const { client } = useSession();
  const ai = useAiControls();
  const navigate = useNavigate();
  const [mine, setMine] = useState(false);
  const [out, setOut] = useState<'All' | AiOutcome>('All');
  const [fac, setFac] = useState('');
  const feed = useQuery<AiActivityResponse>((signal) => aiActivity(client, { query: { scope: 'everyone' }, signal }), [client, ai?.approvals.data]);
  const me = ai?.status.data?.viewer.userId ?? null;
  const now = Date.now();
  const all = feed.data?.rows ?? [];
  const qf = fac.trim().toLowerCase();
  const base = all.filter((x) => (!mine || (!!me && x.by.id === me)) && (!qf || x.building.name.toLowerCase().includes(qf)));
  const rows = base.filter((x) => out === 'All' || x.outcome === out);
  const cnt = (o: AiOutcome) => base.filter((x) => x.outcome === o).length;
  const decided = base.filter((x) => x.outcome === 'APPROVED' || x.outcome === 'EDITED' || x.outcome === 'DECLINED');
  const unchanged = decided.length ? Math.round((100 * base.filter((x) => x.outcome === 'APPROVED').length) / decided.length) : null;
  const waits = decided.map((x) => x.waitMinutes).filter((w): w is number => w != null).sort((a, b) => a - b);
  const med = waits.length ? waits[Math.floor(waits.length / 2)] : null;
  const paused = !!ai?.approvals.data?.paused;
  const pend = ai?.approvals.data?.items.length ?? 0;
  const ago = (t: string) => {
    const m = Math.max(0, Math.round((now - Date.parse(t)) / 60000));
    return m < 1 ? 'just now' : m < 60 ? m + 'm ago' : m < 1440 ? Math.round(m / 60) + 'h ago' : 'Yesterday ' + hm(t);
  };
  const stats = [
    { k: 'Actions run', v: String(cnt('RAN')), n: 'by Atlas, in scope', line: '#264C7E' },
    { k: 'Approved unchanged', v: unchanged == null ? '–' : unchanged + '%', n: decided.length + (decided.length === 1 ? ' decision' : ' decisions'), line: unchanged != null && unchanged > 90 ? '#B3261E' : '#1F6B52' },
    { k: 'Typical time to decide', v: med == null ? '–' : med + ' min', n: 'median, ' + waits.length + ' decided', line: '#2F5C97' },
    { k: 'Refused', v: String(cnt('REFUSED')), n: 'no access or out of scope', line: '#3C454F' },
    { k: 'Held during a pause', v: String(cnt('HELD') + (paused ? pend : 0)), n: paused ? 'AI is paused now' : 'ran when AI came back', line: '#8A1C16' },
  ];
  const chips = ORDER.filter((o) => o === 'All' || o === out || cnt(o) > 0);
  const where = team ? 'Every building. ' : (feed.data?.buildingName ?? ai?.status.data?.buildingName ?? 'Your building') + ' only. ';
  const foot = where + (feed.data && !feed.data.real ? 'Your own approvals, declines and pauses are live. Earlier rows are sample history until the AI audit feed is connected. Each row is on the Truth Ledger.' : 'Each row is on the Truth Ledger.');
  const open = (x: (typeof rows)[number]) => (e: { preventDefault: () => void }) => {
    e.preventDefault();
    if (x.link.view === 'queue') return onQueue();
    if (x.link.view === 'status') {
      onClose();
      ai?.setStatusOpen(true);
      return;
    }
    if (VIEWS.has(x.link.view)) {
      onClose();
      navigate(pathForView(x.link.view as ViewId));
    }
  };
  return (
    <div data-screen-label="Atlas Activity" style={sx('display:flex;flex-direction:column;gap:12px;min-width:0;')}>
      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(118px,1fr));gap:10px 14px;min-width:0;')}>
        {stats.map((k) => (
          <div key={k.k} style={sx('min-width:0;border-top:2px solid ' + k.line + ';padding-top:7px;')}>
            <div style={sx('font-size:20px;font-weight:800;color:#22272E;letter-spacing:-.01em;font-variant-numeric:tabular-nums;')}>{k.v}</div>
            <div style={sx('font-size:14px;color:#3C454F;font-weight:600;margin-top:2px;')}>{k.k}</div>
            <div style={sx('font-size:14px;color:#54606C;margin-top:2px;line-height:1.35;')}>{k.n}</div>
          </div>
        ))}
      </div>
      {unchanged != null && unchanged > 90 && decided.length >= 5 && (
        <div style={sx('font-size:14px;color:#3C454F;background:rgba(47,92,151,.08);border-radius:8px;padding:7px 10px;line-height:1.45;')}>Over 90% of approvals go through with no edit or decline. That can mean people are approving without reading.</div>
      )}
      <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:6px;')}>
        <a href="#" aria-pressed={mine} onClick={(e) => { e.preventDefault(); setMine(true); }} style={sx(pill(mine))}>Mine</a>
        <a href="#" aria-pressed={!mine} onClick={(e) => { e.preventDefault(); setMine(false); }} style={sx(pill(!mine))}>Everyone</a>
        {team && (
          <input
            type="text"
            aria-label="Search facility"
            value={fac}
            onChange={(e) => setFac(e.target.value)}
            placeholder="Search facility"
            style={sx('flex:1 1 120px;min-width:min(100%,120px);height:28px;padding:0 10px;border:1px solid rgba(34,39,46,.16);border-radius:7px;font-size:16px;font-family:inherit;color:#22272E;background:#FCFAF4;box-sizing:border-box;')}
          />
        )}
      </div>
      <div style={sx('display:flex;gap:6px;flex-wrap:wrap;')}>
        {chips.map((o) => (
          <a key={o} href="#" aria-pressed={out === o} onClick={(e) => { e.preventDefault(); setOut(o); }} style={sx(pill(out === o))}>
            {o === 'All' ? 'All' : word(o)} <span style={sx('opacity:.7;font-variant-numeric:tabular-nums;')}>{String(o === 'All' ? base.length : cnt(o))}</span>
          </a>
        ))}
      </div>
      <div style={sx('display:flex;flex-direction:column;min-width:0;')}>
        {rows.map((x) => (
          <div key={x.id} style={sx('display:flex;align-items:flex-start;gap:10px;padding:9px 0;border-top:1px solid rgba(34,39,46,.08);min-width:0;')}>
            <span style={sx('flex:0 0 74px;display:inline-flex;align-items:center;justify-content:center;height:20px;margin-top:1px;border-radius:5px;font-size:14px;font-weight:700;white-space:nowrap;color:' + OT[x.outcome][0] + ';background:' + OT[x.outcome][1] + ';')}>{word(x.outcome)}</span>
            <div style={sx('flex:1 1 auto;min-width:0;')}>
              <div style={sx('font-size:16px;color:#22272E;line-height:1.45;font-weight:600;')}>{x.text}</div>
              {x.why && <div style={sx('font-size:14px;color:#54606C;margin-top:2px;line-height:1.4;')}>{x.why}</div>}
              <div style={sx('display:flex;align-items:center;gap:6px;flex-wrap:wrap;margin-top:3px;font-size:14px;color:#54606C;')}>
                <span>{ago(x.at)}</span>
                <span>{'·'}</span>
                <span>{(me && x.by.id === me ? 'you' : x.by.name) + ' · ' + x.building.name}</span>
                <span>{'·'}</span>
                <a href="#" onClick={open(x)} style={sx('color:#2F5C97;font-weight:600;text-decoration:none;white-space:nowrap;')}>
                  {x.link.label} {'→'}
                </a>
                <span>{'·'}</span>
                <span style={sx('font-variant-numeric:tabular-nums;')}>{x.ref}</span>
              </div>
            </div>
          </div>
        ))}
        {feed.data && rows.length === 0 && <div style={sx('font-size:16px;color:#54606C;padding:10px 0;border-top:1px solid rgba(34,39,46,.08);')}>Nothing matches these filters.</div>}
        {feed.error && <div role="alert" style={sx('font-size:16px;color:#B3261E;padding:10px 0;border-top:1px solid rgba(34,39,46,.08);')}>{feed.error.message}</div>}
      </div>
      <div style={sx('font-size:14px;color:#54606C;line-height:1.45;text-wrap:pretty;')}>{foot}</div>
    </div>
  );
}
